<!--
@Author: Ethan Dinnen
@Date:   2019-02-27 11:43:17
@Last Modified by:   Ethan Dinnen
@Last Modified time: 2019-02-27 15:05:21
-->

<template lang="pug">
  v-layout.disabled-alternate(row wrap :class="cssClass")
    template(v-if='isAddressFirstSelectorField')
      v-flex.pa-0(xs11)
        v-select(
          v-if='isSingleSelectDropdown'
          v-model='customFieldValue'
          :class="fieldClass"
          :items='dropdownItems'
          :disabled='disabled || shouldDisableSelect'
          :label='fieldLabel'
          :prepend-icon="prependIcon"
          @click:prepend="handlePrependAction"
        )
      v-flex(xs1)
        slot(name="action")
    v-flex(v-else).pa-0(xs12)
      //- conditionally display datepicker
      v-menu(
        v-if='isDatePicker'
        offset-y
        v-model="menu"
        :full-width="fullWidth"
        min-width="290px"
        lazy
        :close-on-content-click="false"
      )
        v-text-field(
          v-model='customFieldValue'
          slot='activator'
          maxlength="10"
          :hint="!isProfileV2 ? 'Format: MM-DD-YYYY' : ''"
          persistent-hint
          clearable
          :class="fieldClass"
          :error="!isFieldValid"
          :disabled="disabled"
          :rules="[v => isFieldValid || 'Incorrect date format', v => isPastDate || 'Please enter a date before today', ...validations]"
          :label='fieldLabel'
          :prepend-icon="prependIcon"
          :hide-details="hideDetails"
          @click:clear="clearInput()"
          @click:prepend="handlePrependAction"
          @blur="date = parseDate(customFieldValue)"
        )
        v-date-picker(
          v-if='isFieldValid'
          v-model='date'
          no-title
          :disabled="disabled"
          :show-current='value || true'
          :max="yesterday"
          @change="setDateInCustomField($event)"
        )

      //- display a single select dropdown
      v-select(
        v-else-if='isSingleSelectDropdown'
        v-model='customFieldValue'
        :class="fieldClass"
        :items='dropdownItems'
        :disabled='disabled || shouldDisableSelect'
        :label='fieldLabel'
        :prepend-icon="prependIcon"
        @click:prepend="handlePrependAction"
      )

      //- display a multi select dropdown
      v-select(
        v-else-if='isMultiSelectDropdown'
        v-model='customFieldValue'
        multiple
        chips
        deletable-chips
        :items='dropdownItems'
        :disabled='disabled || shouldDisableSelect'
        :class="fieldClass"
        :label='fieldLabel'
        :prepend-icon="prependIcon"
        @click:prepend="handlePrependAction"
      )

      //- display a switch
      v-switch(
        v-else-if="isSwitch"
        v-model="switchValue"
        color="primary"
        :label="switchLabel"
        :prepend-icon="prependIcon"
      )

      //- else just display a normal input field (autofill prevented)
      v-text-field(
        v-else
        v-model='customFieldValue'
        ref="inputField"
        browser-autocomplete="disable"
        :hide-details="hideDetails"
        :placeholder="fieldPlaceholder"
        :disabled="disabled"
        :class="fieldClass"
        :rules="validations"
        :label='mailUnsubscribed ? fieldLabel + " (Unsubscribed)" : fieldLabel'
        :prepend-icon="prependIcon"
        :error='errorTypes.includes(fieldLabel)'
        :error-messages='errorTypes.includes(fieldLabel) ? [errorMessages[fieldLabel]] : []'
        @click:prepend="handlePrependAction"
      )
    //- todo comment out til post MVP
    //- v-flex(xs2 v-show='showPhoneActions')
    //-   // show phone action icons if not in edit mode
    //-   v-layout(row wrap)
    //-     v-flex.pa-0.text-xs-right(xs5 v-for='icon in phoneIcons[fieldLabel]' :key='icon')
    //-       v-btn.px-1(small outline color='success')
    //-         v-icon(small) {{ icon }}
</template>

<script>
import { 
  formatDateMonthDayYear,
  isYearMonthDayDateFormatValid,
  isBeforeToday,
} from '@/Core/filters/dates';

export default {
  props: {
    mailUnsubscribed: {
      type: Boolean,
    },
    isContactOwner: {
      type: Boolean,
      default: false,
    },
    fieldName: {
      type: String,
    },
    fieldLabels: {
      type: Object,
    },
    fieldType: {
      type: String,
    },
    fieldLabel: {
      type: String,
    },
    value: {
      default: null,
    },
    dropdownItems: {
      type: Array,
    },
    switchOffLabel: {
      type: String,
    },
    isSwitchInverted: {
      type: Boolean,
    },
    validations: {
      type: Array,
      default: () => [],
    },
    prependIcon: {
      type: String,
      default: '',
    },
    prependAction: {
      type: Function,
      required: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    errorTypes: {
      type: Array,
      default: () => [],
    },
    errorMessages: {
      type: Object,
      default: () => {},
    },
    isCustomField: {
      type: Boolean,
      default: false,
    },
    isProfileV2: {
      type: Boolean,
      default: false,
    },
    fullWidth: {
      type: Boolean,
      default: false,
    },
    isAddressField: {
      type: Boolean,
      default: false,
    },
    columnName: {
      type: String,
    },
    hideDetails: {
      type: Boolean,
    },
    fieldPlaceholder: {
      type: String,
    },
  },
  data() {
    return {
      menu: false,
      isFieldValid: true,
      customFieldValue: this.value,
      phoneIcons: {
        mobile: ['chat_bubble', 'stay_current_portrait'],
        home: ['phone'],
        office: ['phone'],
      },
      isPastDate: true,
      date: null,
    };
  },
  created() {
    if (!(this.isDatePicker && this.value)) return;

    if (this.isValidDate(this.value)) {
      this.customFieldValue = this.isFirefox ? this.value : this.formattedDate(this.value);
    } else {
      this.isFieldValid = false;
    }
  },
  computed: {
    /**
     * Checks if the label is for the owner user, and if it
     * is then check that the user is the contact's owner, otherwise disable
     * the select
     * @return {Boolean} true if user is the contact's owner and label is Owner
     */
    shouldDisableSelect() {
      return this.fieldLabel === this.fieldLabels.Owner && !this.isContactOwner;
    },
    isSingleSelectDropdown() {
      return this.fieldType === 'select';
    },
    isMultiSelectDropdown() {
      return this.fieldType === 'multi_select';
    },
    isDatePicker() {
      return ['date', 'Date'].includes(this.fieldType);
    },
    isSwitch() {
      return this.fieldType === 'switch';
    },
    showPhoneActions() {
      const phoneTypes = ['mobile', 'home', 'office'];
      return phoneTypes.includes(this.fieldLabel);
    },
    isActionable() {
      return (
        (this.prependIcon === 'email' || this.prependIcon === 'smartphone') && this.value !== null
      );
    },
    cssClass() {
      let cssClass = !this.isProfileV2 ? 'contact-field' : 'contact-field-v2';
      if (this.prependAction && this.isActionable) {
        cssClass += ' highlight-icon';
      }
      return cssClass;
    },
    fieldClass() {
      if (!this.isProfileV2) return;
      let ccsStyle = 'pa-0 ma-0 font-size';
      const isDateFieldValid = this.isPastDate && this.isDatePicker || this.isCustomField;
      const fieldCanHaveError = this.isDatePicker || this.isCustomField;
      if ((!this.isFieldValid || !isDateFieldValid) && fieldCanHaveError) {
        ccsStyle += ' pb-3';
      }
      return ccsStyle;
    },
    switchLabel() {
      if (this.isSwitchInverted) {
        return this.value ? this.switchOffLabel : this.fieldLabel;
      }
      return this.value ? this.fieldLabel : this.switchOffLabel;
    },
    yesterday() {
      if(this.isCustomField) return;
      return new Date(new Date().setDate(new Date().getDate()-1)).toISOString().substr(0, 10);
    },
    switchValue: {
      get() {
        if (this.isSwitchInverted) {
          return !this.value;
        }
        return this.value;
      },
      set(newValue) {
        if (this.isSwitchInverted) {
          this.$emit('input', !newValue);
        } else {
          this.$emit('input', newValue);
        }
      },
    },
    isAddressFirstSelectorField() {
      return this.isAddressField && this.columnName === "type" && this.isProfileV2;
    },
    isFirefox() {
      return navigator.userAgent.indexOf("Firefox") != -1;
    }
  },
  watch: {
    value(newValue) {
      if (this.isDatePicker && newValue) {
        this.isPastDate = isBeforeToday(newValue) || this.isCustomField;
        newValue = this.formatPreviousDateFormat(newValue);
        this.isFieldValid = isYearMonthDayDateFormatValid(newValue);
        this.customFieldValue = this.maskDate(newValue);
        this.date = this.parseDate(this.customFieldValue);
        this.value = this.customFieldValue;
      } else {
        this.customFieldValue = newValue;
      }
      if (!newValue) {
        if (this.isDatePicker) {
          this.isFieldValid = true;
          this.isPastDate = true;
        }
        this.$emit('clear-field');
      }

      this.$emit('field-status', {
        name: this.fieldName || this.fieldLabel,
        isValid: this.isFieldValid && this.isPastDate,
      });
    },
    customFieldValue(newValue) {
      this.menu = false; // close the datepicker
      if (this.isProfileV2 && this.isCustomField && this.validations.length 
        && typeof this.validations[0] === 'function') {
          const validation = this.validations[0](newValue);
          this.isFieldValid = validation === true;
      }
     
      if(this.isDatePicker && this.formattedDate(this.value) === this.formattedDate(newValue)){
        return;
      }
      // Return without emitting if this is the first load or content is the same
      if (this.value === newValue || newValue === undefined) {
        return;
      }
      this.$emit('input', newValue);
    },
  },
  methods: {
    maskDate(date = '') {
      if (date) {
        let v = date.replace(/\D/g,'').slice(0, 10);
        
        if (v.length >= 5) {
          return `${v.slice(0,2)}-${v.slice(2,4)}-${v.slice(4,8)}`;
        } else if (v.length >= 2) {
          return `${v.slice(0,2)}-${v.slice(2)}`;
        }
  
        return v;
      }

      return '';
    },
    formattedDate(rawDate) {
      if (!rawDate) return;
      return formatDateMonthDayYear(rawDate || this.value);
    },
    handlePrependAction() {
      if (this.prependAction && this.isActionable) {
        this.prependAction(this.value);
      }
    },
    focus() {
      this.$refs.inputField.focus();
    },
    clearInput() {
      this.customFieldValue = null;
      this.isFieldValid = false;
      this.date = null;
    },
    formatPreviousDateFormat(date) {
      if (!date) return null;
      const PREVIOUS_FORMAT_REGEX = /^\d{4}-(0[1-9]|10|[0-1][1-2])-(0[1-9]|10|[1-2][0-9]|3[0-1])$/;
      const splitDate = date.split(/\D/g);

      if (splitDate.length > 3) {
        date = `${splitDate[0]}-${splitDate[1]}-${splitDate[2]}`;
      }

      if (PREVIOUS_FORMAT_REGEX.test(date)) {
        return `${splitDate[1]}-${splitDate[2]}-${splitDate[0]}`
      }

      if (splitDate.length == 3) {
        const MONTH = splitDate[0].length == 1 ? `0${splitDate[0]}` : splitDate[0];
        const DAY = splitDate[1].length == 1 ? `0${splitDate[1]}` : splitDate[1];
        
        return `${MONTH}-${DAY}-${splitDate[2]}`
      }
      return date;
    },
    parseDate(date) {
      if (date && date.split('-').length > 2) {
        const [month, day, year] = date.split('-');
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
      return null;
    },
    setDateInCustomField(e) {
      this.customFieldValue = this.formattedDate(e);
    },
    isValidDate(value) {
      return !isNaN(new Date(this.isFirefox ? value.replace(/-/g, ' ') : value).getTime());
    },
  },
};
</script>
<style lang="scss">
.v-input__slot {
  margin-bottom: 5px;
}
.contact-field-v2 {
  .v-text-field__slot > input {
    padding: 1px;
  }
  .v-select__selection--comma {
    margin: 0;
  }
  .v-select__selections > input {
    padding: 0;
  }
  .v-select.v-select--chips .v-select__selections {
    min-height: 0;
  }
}
.contact-field {
  .v-menu__activator.v-menu__activator--disabled {
    cursor: default;
  }
  .v-text-field > .v-input__control > .v-input__slot:before {
    display: none !important;
  }
  &.highlight-icon {
    i {
      color: var(--v-primary-base);
    }
  }
  &:not(.highlight-icon) {
    i {
      cursor: default !important;
    }
  }
  .v-text-field
    > .v-input__control
    > .v-text-field__details
    > .v-messages
    > .v-messages__wrapper
    > .v-messages__message {
    line-height: 2;
  }
}
.contact-field-v2 {
  .font-size {
    font-size: 14px;
  }
  &.highlight-icon {
    i {
      font-size: 18px;
      color: var(--v-primary-base);
    }
  }
  &:not(.highlight-icon) {
    i {
      font-size: 18px;
      cursor: default !important;
    }
  }

  &.disabled-alternate .theme--light.v-text-field.v-input--is-disabled .v-input__slot:before {
    border-style: solid;
    border-width: thin 0 0 0;
    border-top-color: rgba(0,0,0,0.42);
  }
  &.disabled-alternate .theme--light.v-input--is-disabled input{
    color: rgba(0,0,0,0.54);
    -webkit-text-fill-color: rgba(0,0,0,0.54);
  }
}
</style>
